import {
  APPRAISAL_STEP,
  APPRAISAL_TARGETS,
  HIDDEN_PATTERNS,
  type AppraisalTarget,
  type PatternName,
} from '../../../engine';
import type { NightRun } from '../../../campaign';
import { t } from '../../../i18n';
import { itemName, patternName } from '../../../app/describe';

/**
 * The rate card (docs/LONG_NIGHT_ART_DIRECTION.md §12.4): a small paper price list under the
 * Mantel showing each pattern and feat, its level in numerals, and what its Appraisals add to a
 * chain that scores it. Hidden patterns show as ??? until they are discovered.
 */

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
const roman = (n: number): string => ROMAN[n - 1] ?? String(n);

/** A pattern or feat by name: the patterns as the match names them. */
function rateName(id: AppraisalTarget): string {
  switch (id) {
    case 'ladder':
    case 'echo':
    case 'streak':
    case 'stair':
    case 'prism':
    case 'mirror':
      return patternName(id as PatternName);
    case 'fullCircle':
      return t('preview.fullCircle');
    default:
      return t(`night.rate.${id}`);
  }
}

export function RateCard({ run }: { run: NightRun }) {
  return (
    <table className="rate-card">
      <caption className="sr-only">{t('night.rate.title')}</caption>
      <tbody>
        {APPRAISAL_TARGETS.map((id) => {
          const hidden =
            (HIDDEN_PATTERNS as readonly string[]).includes(id) &&
            !run.discovered.includes(id as (typeof HIDDEN_PATTERNS)[number]);
          const lvl = run.appraisals[id] ?? 1;
          const step = APPRAISAL_STEP[id];
          const add = lvl - 1;
          const pays = [
            step.p && add ? `+${step.p * add} P` : '',
            step.m && add ? `+${step.m * add} M` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <tr key={id} className={`${hidden ? 'is-hidden' : ''} ${add > 0 ? 'is-raised' : ''}`}>
              <th scope="row" title={hidden ? undefined : itemName(`appraisal.${id}`)}>
                {hidden ? '???' : rateName(id)}
              </th>
              <td className="rate-level">{hidden ? '' : roman(lvl)}</td>
              <td className="rate-pays">{hidden ? '' : pays || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
